import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { playSound } from '../../audio/sounds.js';
import type { ArenaSocket } from '../../realtime.js';
import {
  DICE_COUNT,
  LOWER,
  UPPER,
  scoreFor,
  totalScore,
  upperBonus,
  upperSum,
  type Category,
} from '../../../shared/games/dice/rules.js';
import type { DiceView } from '../../../shared/games/dice/view.js';
import type { ArenaResult, MultiGameStateView } from '../../../shared/protocol.js';

const PIPS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[26, 26], [50, 50], [74, 74]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[26, 26], [74, 26], [50, 50], [26, 74], [74, 74]],
  6: [[28, 24], [72, 24], [28, 50], [72, 50], [28, 76], [72, 76]],
};

function Die(props: { value: number | null; held: boolean; rolling: boolean; disabled: boolean; index: number; onToggle: () => void }) {
  const label = props.value ? t.dice.die(props.index + 1, props.value, props.held) : '';
  return (
    <button
      class={`die ${props.held ? 'held' : ''} ${props.rolling ? 'rolling' : ''} ${props.value ? '' : 'blank'}`}
      disabled={props.disabled}
      onClick={props.onToggle}
      aria-pressed={props.held}
      aria-label={label}
      title={label}
    >
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <rect x="4" y="4" width="92" height="92" rx="20" class="die-face" />
        {props.value && PIPS[props.value]!.map(([x, y]) => <circle cx={x} cy={y} r="9" class="die-pip" />)}
      </svg>
      {props.held && <span class="die-held">{t.dice.held}</span>}
    </button>
  );
}

/**
 * Nopparalli (decision 2026-09-27): dice tray and score sheet. Tap dice to keep them, roll up
 * to three times, then choose a category; possible points are shown in your column.
 */
export function DiceGame(props: { state: MultiGameStateView<DiceView>; socket: ArenaSocket; myId: number }) {
  const { state, socket, myId } = props;
  const view = state.module;
  const myTurn = state.turn?.userId === myId;
  const current = state.players.find((p) => p.id === state.turn?.userId);
  const [held, setHeld] = useState<boolean[]>(Array(DICE_COUNT).fill(false));
  const [rolling, setRolling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmZero, setConfirmZero] = useState<Category | null>(null);

  // Held dice follow the server after every roll; a new turn clears them.
  const diceKey = `${state.turn?.userId}-${state.turn?.startedAt}-${view.rollsLeft}`;
  const prevKey = useRef(diceKey);
  useEffect(() => {
    setHeld(view.dice ? view.held : Array(DICE_COUNT).fill(false));
    if (prevKey.current !== diceKey && view.dice && view.rollsLeft < 3) {
      setRolling(true);
      playSound('diceRoll');
      const timer = setTimeout(() => setRolling(false), 650);
      prevKey.current = diceKey;
      return () => clearTimeout(timer);
    }
    prevKey.current = diceKey;
  }, [diceKey]);

  const lastKey = view.lastScore ? `${view.lastScore.userId}-${view.lastScore.category}` : '';
  const firstScore = useRef(true);
  useEffect(() => {
    if (!firstScore.current && lastKey) playSound('diceScore');
    firstScore.current = false;
  }, [lastKey]);

  const send = (payload: unknown) => {
    setBusy(true);
    setError(null);
    socket.emit('multi:move', payload, (r: ArenaResult) => {
      setBusy(false);
      if (!r.ok) setError(t.arena.errors[r.error]);
    });
  };
  const canRoll = myTurn && view.rollsLeft > 0 && !busy;
  const roll = () => canRoll && send({ type: 'roll', held });
  const canHold = myTurn && Boolean(view.dice) && view.rollsLeft > 0 && !busy;
  const toggle = (i: number) => {
    if (!canHold) return;
    playSound('diceHold');
    setHeld(held.map((h, j) => (j === i ? !h : h)));
  };
  const choose = (category: Category) => {
    if (!myTurn || !view.dice || busy) return;
    if (scoreFor(category, view.dice) === 0) return setConfirmZero(category);
    send({ type: 'score', category });
  };

  // Keyboard: 1–5 keep a die, Enter or space rolls.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select')) return;
      if (/^[1-5]$/.test(e.key)) toggle(Number(e.key) - 1);
      else if ((e.key === 'Enter' || e.key === ' ') && canRoll) {
        e.preventDefault();
        roll();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  });

  const players = state.players;
  const sheetOf = (id: number) => view.sheets[id] ?? {};
  const cell = (playerId: number, category: Category) => {
    const value = sheetOf(playerId)[category];
    if (value !== undefined) return <td class={`filled ${value === 0 ? 'zero' : ''}`}>{value === 0 ? '–' : value}</td>;
    if (playerId === myId && myTurn && view.dice) {
      const points = scoreFor(category, view.dice);
      return (
        <td class={`option ${points > 0 ? 'gain' : 'nil'}`}>
          <button
            class={`score-option ${points === 0 ? 'zero' : ''}`}
            disabled={busy}
            onClick={() => choose(category)}
            title={t.dice.scoreConfirm(t.dice.categories[category], points)}
            aria-label={t.dice.scoreConfirm(t.dice.categories[category], points)}
          >
            {points}
          </button>
        </td>
      );
    }
    return <td class="open" />;
  };
  const lastWho = view.lastScore ? players.find((p) => p.id === view.lastScore!.userId)?.displayName : null;

  return (
    <div class="dice-layout">
      <div class="card dice-tray">
        <div class={`dice-row ${myTurn ? 'mine' : ''}`}>
          {Array.from({ length: DICE_COUNT }, (_, i) => (
            <Die
              index={i}
              value={view.dice ? view.dice[i]! : null}
              held={Boolean(view.dice) && held[i]!}
              rolling={rolling && !held[i]}
              disabled={!canHold}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
        {myTurn ? (
          <>
            <button class="btn btn-primary roll-btn" disabled={!canRoll} onClick={roll}>
              🎲 {view.dice ? t.dice.roll : t.dice.rollFirst}
              <span class="rolls-left">{view.rollsLeft > 0 ? t.dice.rollsLeft(view.rollsLeft) : t.dice.noRolls}</span>
            </button>
            <p class="muted dice-hint">{view.dice ? (view.rollsLeft > 0 ? `${t.dice.holdHint} ${t.dice.chooseHint}` : t.dice.chooseHint) : ''}</p>
          </>
        ) : (
          <p class="muted dice-hint">{current ? t.dice.waitTurn(current.displayName) : ''}</p>
        )}
        {view.lastScore && lastWho && (
          <p class="last-score" key={lastKey}>
            {t.dice.scored(lastWho, t.dice.categories[view.lastScore.category], view.lastScore.points)}
          </p>
        )}
        {error && <p class="error">{error}</p>}
      </div>

      <div class="card score-card">
        <div class="score-scroll">
          <table class="score-table">
            <thead>
              <tr>
                <th class="cat-col">{t.dice.category}</th>
                {players.map((p) => (
                  <th
                    class={`${p.id === state.turn?.userId ? 'current' : ''} ${p.id === myId ? 'me' : ''} ${p.dropped ? 'dropped' : ''}`}
                    title={p.displayName}
                  >
                    <span class="player-col-name">
                      {p.isAi && '🤖 '}
                      {p.displayName}
                    </span>
                    {p.dropped && <span class="col-note">{t.multi.left}</span>}
                    {!p.dropped && !p.online && <span class="col-note">{t.multi.offline}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {UPPER.map((c) => (
                <tr>
                  <th class="cat-col">{t.dice.categories[c]}</th>
                  {players.map((p) => cell(p.id, c))}
                </tr>
              ))}
              <tr class="sum-row">
                <th class="cat-col">{t.dice.upperSum}</th>
                {players.map((p) => (
                  <td>{upperSum(sheetOf(p.id))}</td>
                ))}
              </tr>
              <tr class="sum-row">
                <th class="cat-col">{t.dice.bonus}</th>
                {players.map((p) => (
                  <td>{upperBonus(sheetOf(p.id)) || ''}</td>
                ))}
              </tr>
              {LOWER.map((c) => (
                <tr>
                  <th class="cat-col">{t.dice.categories[c]}</th>
                  {players.map((p) => cell(p.id, c))}
                </tr>
              ))}
              <tr class="total-row">
                <th class="cat-col">{t.dice.total}</th>
                {players.map((p) => (
                  <td>{totalScore(sheetOf(p.id))}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {confirmZero && (
        <ConfirmDialog
          title={t.dice.zeroConfirm(t.dice.categories[confirmZero])}
          confirmLabel={t.dice.scoreConfirm(t.dice.categories[confirmZero], 0)}
          cancelLabel={t.game.cancel}
          onConfirm={() => {
            const c = confirmZero;
            setConfirmZero(null);
            send({ type: 'score', category: c });
          }}
          onCancel={() => setConfirmZero(null)}
        >
          <p>{t.dice.chooseHint}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
